/**
 * sg-motion.js — Soul & Gold animation layer
 * GSAP + ScrollTrigger, init by page context (index / collection / product).
 * Graceful degradation: prefers-reduced-motion skips all animations.
 * Theme Editor: reinit/cleanup on shopify:section:load, reorder, block:select.
 */

(function () {
  'use strict';

  /* ── Guards ──────────────────────────────────────────────── */
  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (REDUCED) return;

  gsap.registerPlugin(ScrollTrigger);

  /* ── Shared defaults ─────────────────────────────────────── */
  const D = {
    ease: 'power2.out',
    dur: 0.85,
    durShort: 0.7,
    y: () => (window.innerWidth >= 750 ? 32 : 24),
    stagger: 0.1,
    staggerTight: 0.08,
  };

  const scrollDefaults = {
    start: 'top 88%',
    once: true,
  };

  /* ── Helpers ─────────────────────────────────────────────── */
  function reveal(targets, vars = {}) {
    if (!targets || (targets.length !== undefined && targets.length === 0)) return null;
    const els = typeof targets === 'string' ? document.querySelectorAll(targets) : targets;
    if (!els || (els.length !== undefined && els.length === 0)) return null;

    gsap.set(els, { opacity: 0, y: D.y(), ...vars.from });

    return ScrollTrigger.batch(els, {
      ...scrollDefaults,
      onEnter: (batch) =>
        gsap.to(batch, {
          opacity: 1,
          y: 0,
          duration: D.dur,
          ease: D.ease,
          stagger: D.stagger,
          ...vars.to,
        }),
    });
  }

  function revealImmediate(targets, vars = {}) {
    if (!targets || (targets.length !== undefined && targets.length === 0)) return null;
    const els = typeof targets === 'string' ? document.querySelectorAll(targets) : targets;
    if (!els || (els.length !== undefined && els.length === 0)) return null;

    gsap.set(els, { opacity: 0, y: D.y(), ...vars.from });
    return gsap.to(els, {
      opacity: 1,
      y: 0,
      duration: D.dur,
      ease: D.ease,
      stagger: { each: D.stagger, ...vars.stagger },
      delay: vars.delay || 0,
      ...vars.to,
    });
  }

  function scaleReveal(target, vars = {}) {
    if (!target) return null;
    gsap.set(target, { scale: 1.04, opacity: 0, ...vars.from });
    return ScrollTrigger.create({
      trigger: target,
      ...scrollDefaults,
      onEnter: () =>
        gsap.to(target, {
          scale: 1,
          opacity: 1,
          duration: D.dur,
          ease: D.ease,
          ...vars.to,
        }),
    });
  }

  function headingPlusGrid(section, gridItemSelector) {
    if (!section) return;
    const heading = section.querySelector('[data-sg-motion-group="heading"]');
    const items = section.querySelectorAll(gridItemSelector);
    if (heading) reveal([heading], { to: { duration: D.durShort } });
    if (items.length) reveal(items, { to: { stagger: D.stagger } });
  }

  /* ── Home ────────────────────────────────────────────────── */
  function initHomeMotion() {
    /* Hero — immediate (above the fold) */
    const hero = document.querySelector('[data-sg-motion="hero"]');
    if (hero) {
      const media = hero.querySelector('[data-sg-motion-group="media"]');
      const content = hero.querySelector('[data-sg-motion-group="content"]');
      const items = hero.querySelectorAll('[data-sg-motion-item]');

      if (media) {
        gsap.set(media, { scale: 1.04, opacity: 0 });
        gsap.to(media, { scale: 1, opacity: 1, duration: 1.1, ease: 'power2.out', delay: 0.1 });
      }
      if (items.length) {
        revealImmediate(items, { delay: 0.25, stagger: { each: D.stagger } });
      } else if (content) {
        revealImmediate([content], { delay: 0.25 });
      }
    }

    /* Featured collection (may be multiple instances, e.g. "Lo más nuevo" + "Más vendidos") */
    document.querySelectorAll('[data-sg-motion="featured-collection"]').forEach((fc) => {
      headingPlusGrid(fc, '[data-sg-motion-item]');
    });

    /* Promo cards */
    const fpcCards = document.querySelectorAll('[data-sg-motion="promo-cards"] [data-sg-motion-item]');
    fpcCards.forEach((card) => {
      const media = card.querySelector('[data-sg-motion-group="media"]');
      const content = card.querySelector('[data-sg-motion-group="content"]');
      if (media) scaleReveal(media);
      if (content) reveal([content], { to: { duration: D.durShort } });
    });

    /* Category list */
    const cl = document.querySelector('[data-sg-motion="category-list"]');
    if (cl) headingPlusGrid(cl, '[data-sg-motion-item]');

    /* Testimonials */
    const tm = document.querySelector('[data-sg-motion="testimonials"]');
    if (tm) headingPlusGrid(tm, '[data-sg-motion-item]');

    /* Info section */
    const info = document.querySelector('[data-sg-motion="info-section"]');
    if (info) {
      const heading = info.querySelector('[data-sg-motion-group="heading"]');
      const cards = info.querySelectorAll('[data-sg-motion-group="cards"] [data-sg-motion-item]');
      const chips = info.querySelectorAll('[data-sg-motion-group="chips"] [data-sg-motion-item]');
      if (heading) reveal([heading], { to: { duration: D.durShort } });
      if (cards.length) {
        reveal(cards, { to: { stagger: D.stagger } });
        /* micro-scale on icons */
        cards.forEach((card) => {
          const icon = card.querySelector('.sg-is-card__badge');
          if (icon) {
            gsap.set(icon, { scale: 0.7, opacity: 0 });
            ScrollTrigger.create({
              trigger: card,
              ...scrollDefaults,
              onEnter: () =>
                gsap.to(icon, { scale: 1, opacity: 1, duration: 0.5, ease: 'back.out(1.4)', delay: 0.15 }),
            });
          }
        });
      }
      if (chips.length) reveal(chips, { to: { stagger: D.staggerTight } });
    }

    /* Instagram feed — only shell elements, not app block DOM */
    const ig = document.querySelector('[data-sg-motion="instagram"]');
    if (ig) {
      const header = ig.querySelector('[data-sg-motion-group="heading"]');
      const cta = ig.querySelector('[data-sg-motion-group="cta"]');
      if (header) reveal([header], { to: { duration: D.durShort } });
      if (cta) reveal([cta], { to: { duration: D.durShort } });
    }

    /* Video section */
    const video = document.querySelector('[data-sg-motion="video"]');
    if (video) {
      reveal([video], { to: { duration: D.durShort } });
      const playBtn = video.querySelector('.deferred-media__poster-button');
      if (playBtn) {
        gsap.set(playBtn, { scale: 0.88, opacity: 0 });
        ScrollTrigger.create({
          trigger: video,
          ...scrollDefaults,
          onEnter: () =>
            gsap.to(playBtn, { scale: 1, opacity: 1, duration: 0.55, ease: 'back.out(1.5)', delay: 0.3 }),
        });
      }
    }
  }

  /* ── Collection ──────────────────────────────────────────── */
  function initCollectionMotion() {
    /* Collection hero */
    const cHero = document.querySelector('[data-sg-motion="collection-hero"]');
    if (cHero) {
      const media = cHero.querySelector('[data-sg-motion-group="media"]');
      const items = cHero.querySelectorAll('[data-sg-motion-item]');
      if (media) {
        gsap.set(media, { scale: 1.04, opacity: 0 });
        gsap.to(media, { scale: 1, opacity: 1, duration: 1.0, ease: 'power2.out', delay: 0.1 });
      }
      if (items.length) revealImmediate(items, { delay: 0.2, stagger: { each: D.staggerTight } });
    }

    /* Catalog */
    const catalog = document.querySelector('[data-sg-motion="catalog"]');
    if (catalog) {
      const topBar = catalog.querySelector('[data-sg-motion-group="tabs"]');
      const count = catalog.querySelector('[data-sg-motion-group="count"]');
      const items = catalog.querySelectorAll('[data-sg-motion-item]');

      if (topBar) reveal([topBar], { to: { duration: D.durShort } });
      if (count) reveal([count], { to: { duration: D.durShort } });
      if (items.length) reveal(items, { to: { stagger: D.stagger } });
    }

    /* Collection CTA */
    const cta = document.querySelector('[data-sg-motion="catalog-cta"]');
    if (cta) {
      const items = cta.querySelectorAll('[data-sg-motion-item]');
      if (items.length) reveal(items, { to: { stagger: D.staggerTight } });
    }
  }

  /* ── Product (PDP) ───────────────────────────────────────── */
  function initProductMotion() {
    /* Main product — media column */
    const gallery = document.querySelector('[data-sg-motion="pdp-main"] [data-sg-motion-group="media"]');
    if (gallery) {
      gsap.set(gallery, { opacity: 0, y: D.y() });
      gsap.to(gallery, { opacity: 1, y: 0, duration: D.dur, ease: D.ease, delay: 0.15 });
    }

    /* Thumbnails — use existing Dawn class, no hook needed in the snippet */
    const thumbsList = document.querySelector('[data-sg-motion="pdp-main"] [data-sg-motion-group="media"] .thumbnail-list');
    if (thumbsList) {
      const thumbs = Array.from(thumbsList.children).filter(
        (el) => !el.classList.contains('thumbnail-list_item--variant')
      );
      if (thumbs.length > 1) {
        gsap.set(thumbs, { opacity: 0, y: 10 });
        gsap.to(thumbs, {
          opacity: 1,
          y: 0,
          duration: D.durShort,
          ease: D.ease,
          stagger: D.staggerTight,
          delay: 0.35,
        });
      }
    }

    /* Main product — info column (all direct children of the info container) */
    const infoContainer = document.querySelector('[data-sg-motion="pdp-main"] [data-sg-motion-group="content"]');
    if (infoContainer) {
      const infoItems = Array.from(infoContainer.children);
      if (infoItems.length) {
        gsap.set(infoItems, { opacity: 0, y: D.y() });
        gsap.to(infoItems, {
          opacity: 1,
          y: 0,
          duration: D.dur,
          ease: D.ease,
          stagger: D.staggerTight,
          delay: 0.2,
        });
      }
    }

    /* Benefits strip */
    const benefits = document.querySelector('[data-sg-motion="pdp-benefits"]');
    if (benefits) {
      const items = benefits.querySelectorAll('[data-sg-motion-item]');
      if (items.length) {
        reveal(items, { to: { stagger: D.stagger } });
        items.forEach((item) => {
          const icon = item.querySelector('.sg-pdp-benefits-strip__icon');
          if (icon) {
            gsap.set(icon, { scale: 0.75, opacity: 0 });
            ScrollTrigger.create({
              trigger: item,
              ...scrollDefaults,
              onEnter: () =>
                gsap.to(icon, { scale: 1, opacity: 1, duration: 0.45, ease: 'back.out(1.4)', delay: 0.1 }),
            });
          }
        });
      }
    }

    /* Related products */
    const related = document.querySelector('[data-sg-motion="related-products"]');
    if (related) headingPlusGrid(related, '[data-sg-motion-item]');

    /* Promo cards (same pattern as Home) */
    const fpcCards = document.querySelectorAll('[data-sg-motion="promo-cards"] [data-sg-motion-item]');
    fpcCards.forEach((card) => {
      const media = card.querySelector('[data-sg-motion-group="media"]');
      const content = card.querySelector('[data-sg-motion-group="content"]');
      if (media) scaleReveal(media);
      if (content) reveal([content], { to: { duration: D.durShort } });
    });
  }

  /* ── Boot ────────────────────────────────────────────────── */
  function boot() {
    const ctx = window.sgMotionContext;
    if (ctx === 'index') initHomeMotion();
    else if (ctx === 'collection') initCollectionMotion();
    else if (ctx === 'product') initProductMotion();
  }

  /* ── Cleanup helper for Theme Editor ─────────────────────── */
  function killAndRefresh() {
    ScrollTrigger.getAll().forEach((t) => t.kill());
    gsap.globalTimeline.clear();
    /* Small delay so Shopify finishes DOM update */
    setTimeout(boot, 120);
  }

  /* ── Theme Editor events ─────────────────────────────────── */
  document.addEventListener('shopify:section:load', killAndRefresh);
  document.addEventListener('shopify:section:reorder', killAndRefresh);
  document.addEventListener('shopify:block:select', killAndRefresh);

  /* ── Init on DOMContentLoaded ────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
